/**
 * Service worker registration with safe environment checks.
 *
 * The Service Worker spec mandates a "secure context" — HTTPS, localhost, or
 * loopback. Browsers will refuse to register on plain HTTP, so we use the
 * platform's own `window.isSecureContext` flag (the canonical, future-proof
 * way to detect this) instead of hand-rolling a hostname allowlist.
 *
 * This means:
 *   - Production HTTPS deployments: registers (full PWA + offline + push)
 *   - localhost / 127.0.0.1: registers (dev convenience)
 *   - file:// or plain HTTP: silently no-op (browser would refuse anyway)
 *
 * Returns the registration promise so callers (e.g. push opt-in flow) can
 * chain `.pushManager.subscribe()` after the SW is active.
 */

let _registrationPromise = null;

export function registerServiceWorker() {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (!('serviceWorker' in navigator)) {
    console.info('[SW] serviceWorker API not available in this browser');
    return Promise.resolve(null);
  }
  if (!window.isSecureContext) {
    console.info('[SW] Skipping SW registration — not a secure context (HTTPS or localhost required)');
    return Promise.resolve(null);
  }
  if (_registrationPromise) return _registrationPromise;

  _registrationPromise = new Promise((resolve) => {
    const doRegister = () => {
      navigator.serviceWorker
        .register('/service-worker.js', { scope: '/' })
        .then((reg) => {
          console.info('[SW] Registered, scope:', reg.scope);
          reg.addEventListener('updatefound', () => {
            const newSW = reg.installing;
            if (!newSW) return;
            newSW.addEventListener('statechange', () => {
              if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
                console.info('[SW] New version installed — will activate next reload');
              }
            });
          });
          resolve(reg);
        })
        .catch((err) => {
          console.warn('[SW] Registration failed:', err?.message || err);
          resolve(null);
        });
    };
    if (document.readyState === 'complete') {
      doRegister();
    } else {
      window.addEventListener('load', doRegister, { once: true });
    }
  });

  return _registrationPromise;
}

/** Returns the current ServiceWorkerRegistration if one exists, else null. */
export async function getServiceWorkerRegistration() {
  if (!('serviceWorker' in navigator)) return null;
  if (!window.isSecureContext) return null;
  if (_registrationPromise) {
    return _registrationPromise;
  }
  return navigator.serviceWorker.getRegistration?.() ?? null;
}
